import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/hooks/useToast"
import { UpdateChatStatusInput } from "@/modules/admin/capacity/services/__generated__/useUpdateChatStatusMutation.graphql"
import { useUpdateChatStatusMutation } from "@/modules/admin/capacity/services/useUpdateChatStatusMutation"
import { Language } from "@/types/__generated__/union-types"

interface DisabledNewChatsDialogProps {
  isMedical: boolean
  onClose: () => void
  chatStatus: Record<Language, boolean>
}

const DisabledNewChatsDialog = ({ isMedical, onClose, chatStatus }: DisabledNewChatsDialogProps) => {
  const { t } = useTranslation()

  const { control, handleSubmit } = useForm({
    defaultValues: {
      isMedical,
      isEnglishActive: chatStatus["ENGLISH"],
      isFrenchActive: chatStatus["FRENCH"],
      isItalianActive: chatStatus["ITALIAN"],
      isGermanActive: chatStatus["GERMAN"],
    },
  })

  const [updateDisabledStatus] = useUpdateChatStatusMutation()

  const onSubmit = (data: UpdateChatStatusInput) => {
    updateDisabledStatus({
      variables: {
        input: data,
      },
      onCompleted: () => {
        toast({
          description: t("admin.capacity.disabledNewChat.updateSuccessful"),
        })
        onClose()
      },
      onError: (error: Error) => {
        console.error(`An error occurred while update status chat avaibility`, error)
      },
    })
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContentCustom className="min-w-150.5" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("admin.capacity.disabledNewChat.titleDialog")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-row gap-8 px-6 py-2">
            <div className="border-border flex basis-2/4 justify-between rounded-lg border p-2 pr-4 pl-4">
              <span className="mt-1">{t("common.languageList.english")}</span>
              <Controller
                name="isEnglishActive"
                control={control}
                render={({ field }) => (
                  <Switch id="isEnglishActive" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>
            <div className="border-border flex basis-2/4 justify-between rounded-lg border p-2 pr-4 pl-4">
              <span className="mt-1">{t("common.languageList.french")}</span>
              <Controller
                name="isFrenchActive"
                control={control}
                render={({ field }) => (
                  <Switch id="isFrenchActive" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>
          </div>

          <div className="flex flex-row gap-8 px-6 py-2">
            <div className="border-border flex basis-2/4 justify-between rounded-lg border p-2 pr-4 pl-4">
              <span className="mt-1">{t("common.languageList.italian")}</span>
              <Controller
                name="isItalianActive"
                control={control}
                render={({ field }) => (
                  <Switch id="isItalianActive" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>
            <div className="border-border flex basis-2/4 justify-between rounded-lg border p-2 pr-4 pl-4">
              <span className="mt-1">{t("common.languageList.german")}</span>
              <Controller
                name="isGermanActive"
                control={control}
                render={({ field }) => (
                  <Switch id="isGermanActive" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>
          </div>

          <DialogFooter className="flex p-6 pt-4">
            <Button className="rounded-[20px] px-6 py-2.5" variant="secondary" onClick={onClose}>
              {t("common.button.cancel")}
            </Button>
            <Button variant="primary" className="ml-0 rounded-[20px] px-6 py-2.5" type="submit">
              {t("common.button.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContentCustom>
    </Dialog>
  )
}

export { DisabledNewChatsDialog }
